import React, { useEffect, useState } from 'react'
import client from 'api/auth'

declare let ORDERS_SVC_API5: string

// Mirrors the OrderIncentive schema from orders-api5 (tag OrderIncentives)
export interface OrderIncentive {
  id: number
  type: string // known values: 'dollar_discount' | 'meal_deal' | 'loyalty_contract'
  fields: Record<string, any>
  title: string
  inserted_at: string
  description: string | null
  reasoning: string | null
  expires_at: string | null
  redeemed_at: string | null
  redeemed_by: string | null
  from_order_uuid?: string | null // order that generated this incentive (triggering_incentive only)
}

export interface IncentivesResult {
  sent: OrderIncentive[] | null
  triggering: OrderIncentive | null
  loading: boolean
  refetch: () => void
}

export function authJsonHeaders(): Record<string, string> {
  const auth = client.getAuthResponse()
  const token = auth ? auth.access_token : null
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    Authorisation: `Bearer ${token}`,
  }
}

function loadSent(orderUuid: string): Promise<OrderIncentive[] | null> {
  return fetch(`${ORDERS_SVC_API5}/v5/orders/${orderUuid}/sent_incentives`, {
    method: 'GET',
    headers: authJsonHeaders(),
  })
    .then((res: Response) => (res.ok ? res.json() : null))
    .catch(() => null)
}

function loadTriggering(orderUuid: string): Promise<OrderIncentive | null> {
  return fetch(`${ORDERS_SVC_API5}/v5/orders/${orderUuid}/triggering_incentive`, {
    method: 'GET',
    headers: authJsonHeaders(),
  })
    .then((res: Response) => (res.ok ? res.json() : null))
    .catch(() => null)
}

export function useIncentives(orderUuid: string): IncentivesResult {
  const [sent, setSent] = useState<OrderIncentive[] | null>(null)
  const [triggering, setTriggering] = useState<OrderIncentive | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const p1 = loadSent(orderUuid).then(setSent)
    const p2 = loadTriggering(orderUuid).then(setTriggering)
    Promise.all([p1, p2]).finally(() => setLoading(false))
  }, [orderUuid])

  const refetch = (): void => {
    // does not touch `loading`: refetch must never flicker the tab away (Review Focus #1)
    // failed refresh keeps last-known data: wiping already-shown incentives on a
    // transient 401/network error would hide the tab (and this button) until reload
    loadSent(orderUuid).then((v: OrderIncentive[] | null) => v != null && setSent(v))
    loadTriggering(orderUuid).then((v: OrderIncentive | null) => v != null && setTriggering(v))
  }

  return { sent, triggering, loading, refetch }
}

export function redeemIncentive(id: number): Promise<OrderIncentive | null> {
  return fetch(`${ORDERS_SVC_API5}/v5/incentives/${id}/redeem`, {
    method: 'POST',
    headers: authJsonHeaders(),
  })
    .then((res: Response) => (res.ok ? res.json() : null))
    .catch(() => null)
}

export function hasIncentiveData(r: IncentivesResult): boolean {
  return !r.loading && ((r.sent && r.sent.length > 0) || r.triggering != null)
}